// Wanderlist from all of the user's guides (2026-10-23, same file on web and
// mobile). Each guide carries a short summary on its guide_info document
// (attributes.wanderlist), recalculated on every save: its visited and
// bucket-listed places, and its trips from the Main schedule. Wanderlist
// reads every guide's summary and merges them — read only; nothing is ever
// copied into a guide.
//
// version 2 (2026-10-23) adds `visits` (the Main schedule's scheduled place
// visits, for Wanderlist's bar graph); an older summary is filled again.
export const SUMMARY_VERSION = 2;
export const isCurrentSummary = (summary) => !!summary && summary.version >= SUMMARY_VERSION;

// A Main schedule holding more than one trip is split where nothing is
// scheduled for more than this many days.
export const TRIP_GAP_DAYS = 3;

const isoDay = (v) => {
  if (!v) return "";
  if (typeof v === "string") return v.slice(0, 10);
  if (typeof v.toISODate === "function") return v.toISODate() || "";
  if (v instanceof Date) return v.toISOString().slice(0, 10);
  return "";
};
const dayNumber = (d) => Math.floor(Date.parse(`${d}T00:00:00Z`) / 86400000);
const round = (n, digits) => {
  const x = parseFloat(n);
  if (!Number.isFinite(x) || x === 0) return null;
  const f = 10 ** digits;
  return Math.round(x * f) / f;
};

// Same city/country/name in any spelling of case or accents ("Firenze",
// "firenze ") counts once.
export const normalizeName = (s) =>
  String(s || "").normalize("NFD").replace(/[̀-ͯ]/g, "").toLowerCase().replace(/\s+/g, " ").trim();

// The summary saved with a guide. `guide` is a Guide (or its plain JSON).
export const buildGuideSummary = (guide) => {
  const list = guide?.places?.placesList || [];
  const places = [];
  const byId = new Map();
  list.forEach((p) => {
    if (!p) return;
    byId.set(String(p.ID), p);
    if (p.Visited !== true && p.BucketList !== true) return;
    places.push({
      name: String(p.Name || "").slice(0, 80),
      city: p.City || "",
      country: p.Country || "",
      lat: round(p.Lat, 4),
      lng: round(p.Longi, 4),
      ...(p.Visited === true ? { visited: true } : {}),
      ...(p.BucketList === true ? { bucket: true } : {}),
    });
  });

  // Trips: the Main schedule's days, split at gaps of more than TRIP_GAP_DAYS.
  // Visits: each place scheduled in Main, on its day (for the bar graph).
  const days = new Map(); // day -> Set of "city|country"
  const visits = [];
  const seenVisits = new Set();
  (guide?.schedule?.events || []).forEach((e) => {
    const sched = e.Itinerary == null ? "Main" : String(e.Itinerary).replace(/%20/g, " ");
    if (sched !== "Main") return;
    const day = isoDay(e.Start);
    if (!day) return;
    if (!days.has(day)) days.set(day, new Set());
    String(e.Places || "").split(",").forEach((id) => {
      const p = byId.get(id.trim());
      if (!p) return;
      if (p.City && p.Country) days.get(day).add(`${p.City}|${p.Country}`);
      const key = placeKey(p);
      if (seenVisits.has(`${day}|${key}`)) return;
      seenVisits.add(`${day}|${key}`);
      visits.push({ day, key, ...(p.Visited === true ? { visited: true } : {}) });
    });
  });
  const trips = [];
  [...days.keys()].sort().forEach((day) => {
    const last = trips[trips.length - 1];
    if (last && dayNumber(day) - dayNumber(last.end) <= TRIP_GAP_DAYS) last.end = day;
    else trips.push({ start: day, end: day, cities: [] });
    const t = trips[trips.length - 1];
    days.get(day).forEach((key) => {
      const [city, country] = key.split("|");
      if (!t.cities.some((c) => c.city === city && c.country === country)) t.cities.push({ city, country });
    });
  });
  return { version: SUMMARY_VERSION, places, trips, visits };
};

// The identity used to remove duplicates: the name at (about) the same spot
// (3 decimals ≈ 100 m), or the name in the same city/country with no position.
// Works on a summary place ({name, city, country, lat, lng}) or a Place.
export const placeKey = (p) => {
  const lat = round(p.lat ?? p.Lat, 3), lng = round(p.lng ?? p.Longi, 3);
  const pos = lat != null && lng != null ? `${lat},${lng}` : `${normalizeName(p.city ?? p.City)}|${normalizeName(p.country ?? p.Country)}`;
  return `${normalizeName(p.name ?? p.Name)}@${pos}`;
};

// Merges the guides' summaries. sources: [{ guideId, guideName, summary }].
// Places: one per name at (about) the same spot — or the same name in the
// same city when there's no position; visited/bucket-listed if any guide
// says so. Trips: the same dates and cities in two guides (e.g. a guide and
// its copy) count once. Each item lists the guides it came from.
export const mergeWanderlist = (sources) => {
  const places = new Map();
  const trips = new Map();
  const visits = new Map();
  (sources || []).forEach(({ guideId, guideName, summary }) => {
    if (!summary) return;
    const from = { guideId, guideName };
    (summary.places || []).forEach((p) => {
      const key = placeKey(p);
      const m = places.get(key);
      if (!m) {
        places.set(key, { ...p, visited: !!p.visited, bucket: !!p.bucket, guides: [from] });
      } else {
        m.visited = m.visited || !!p.visited;
        m.bucket = m.bucket || !!p.bucket;
        if (!m.city && p.city) m.city = p.city;
        if (!m.country && p.country) m.country = p.country;
        if (!m.guides.some((g) => g.guideId === guideId)) m.guides.push(from);
      }
    });
    (summary.visits || []).forEach((v) => {
      const k = `${v.day}|${v.key}`;
      const m = visits.get(k);
      if (!m) visits.set(k, { ...v, visited: !!v.visited });
      else m.visited = m.visited || !!v.visited;
    });
    (summary.trips || []).forEach((t) => {
      const cities = (t.cities || []).map((c) => `${normalizeName(c.city)}|${normalizeName(c.country)}`).sort().join(";");
      const key = `${t.start}|${t.end}|${cities}`;
      const m = trips.get(key);
      if (!m) trips.set(key, { ...t, guides: [from] });
      else if (!m.guides.some((g) => g.guideId === guideId)) m.guides.push(from);
    });
  });
  return {
    places: [...places.values()],
    trips: [...trips.values()].sort((a, b) => (a.start < b.start ? -1 : a.start > b.start ? 1 : 0)),
    visits: [...visits.values()],
  };
};

// Trips taken (started before today) and planned (today or later).
export const splitTrips = (trips, today = new Date().toISOString().slice(0, 10)) => ({
  taken: (trips || []).filter((t) => t.start < today),
  planned: (trips || []).filter((t) => t.start >= today),
});
